import { useLayoutEffect, useRef, useState } from "react";

// Below this a heading stops shrinking and wraps onto a second line instead.
const MIN_FONT_SIZE_PX = 16;

// A theme size is a number of pixels or a CSS length, "1.5rem" for MUI's own variants.
const toPx = (size) => {
  if (typeof size === "number") return size;
  if (typeof size !== "string") return null;

  const value = parseFloat(size);
  if (Number.isNaN(value)) return null;
  if (size.endsWith("rem")) {
    return value * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  return value;
};

/**
 * A one-line heading that keeps its own size wherever it fits, and shrinks just enough where it
 * does not -- a phone, a narrow card -- rather than running out of its box. Below
 * `MIN_FONT_SIZE_PX` it wraps instead of shrinking further.
 *
 * `boxRef` goes on the heading, `textRef` on an inline-block element round its text: the box gives
 * the room there is, the inner element the width the text needs, clipped or not. Text width grows
 * in step with the font size, so one measurement at any size says what size fits. It measures
 * again whenever either of the two changes size: the box when the window does, the text when the
 * tenant's web font arrives -- often after the page has settled, and wider than the fallback.
 */
const useFitText = (text, baseFontSize) => {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const [fit, setFit] = useState({ fontSize: null, wrap: false });

  useLayoutEffect(() => {
    const box = boxRef.current;
    const span = textRef.current;
    if (!box || !span) return undefined;

    const measure = () => {
      const base = toPx(baseFontSize);
      const current = parseFloat(getComputedStyle(box).fontSize);
      const textWidth = span.getBoundingClientRect().width;
      const room = box.clientWidth;
      if (!base || !current || !textWidth || !room) return;

      const naturalWidth = textWidth * (base / current);
      // A pixel of slack: widths are fractional, `clientWidth` is rounded.
      const fitting = naturalWidth <= room + 1 ? base : base * (room / naturalWidth);
      const next = fitting >= base
        ? { fontSize: null, wrap: false }
        : { fontSize: Math.max(MIN_FONT_SIZE_PX, Math.floor(fitting * 2) / 2), wrap: fitting < MIN_FONT_SIZE_PX };

      setFit((previous) => (previous.fontSize === next.fontSize && previous.wrap === next.wrap ? previous : next));
    };

    measure();

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(span);

    return () => observer.disconnect();
  }, [text, baseFontSize]);

  const fitSx = {
    ...(fit.fontSize ? { fontSize: `${fit.fontSize}px` } : {}),
    ...(fit.wrap ? { whiteSpace: "normal" } : {}),
  };

  return { boxRef, textRef, fitSx };
};

export default useFitText;
